import { usePostHog } from '@posthog/react';
import * as Sentry from '@sentry/browser';
import { UserTypes } from 'common';
import posthog from 'posthog-js';
import { useEffect, useMemo, useState } from 'react';

declare global {
  interface Window {
    vgo?: (action: string, data?: string) => void;
  }
}

import {
  IVerifyUserAdapter,
  UserVerificationError,
} from 'application/modules/auth/useCases/hooks/useCaseVerifyUser';
import useCaseExternalNavigation from 'application/modules/bookingWizard/useCases/hooks/useCaseNavigateTo';
import { WizardStep } from 'domain/entities/WizardStep';
import mboAPI from 'infrastructure/redux/adapters/mboAPI/mboApi';
import { selectAccessToken, selectRefreshToken } from 'infrastructure/redux/slices/auth.selector';
import {
  clearAuthData,
  setAccessToken,
  setRefreshToken,
  setShowError,
  setToken,
} from 'infrastructure/redux/slices/auth.slice';
import {
  selectBookingForm,
  selectEmail,
  selectExperienceType,
  selectLocation,
} from 'infrastructure/redux/slices/bookingWizard.selector';
import { setBookingWizardData, setEmail } from 'infrastructure/redux/slices/bookingWizard.slice';
import { setUserSummary } from 'infrastructure/redux/slices/user.slice';
import { getReduxStoreVersion } from 'infrastructure/redux/store';
import { useAppDispatch, useAppSelector } from 'infrastructure/redux/store/hooks';

import { QueryOrSerializedError } from './SignInAdapter';

export type Vgo = (method: string, context?: string) => void;

declare global {
  const vgo: Vgo | undefined;
}

const getError = (error?: QueryOrSerializedError): UserVerificationError | undefined => {
  if (!error) {
    return undefined;
  }

  if (error?.status === 401) {
    return UserVerificationError.Unauthorized;
  }

  if (error?.status === 400) {
    return UserVerificationError.ValidationError;
  }

  return UserVerificationError.Unknown;
};

const useVerifyUser: IVerifyUserAdapter = () => {
  const posthog = usePostHog();
  const location = useAppSelector(selectLocation);
  const storeEmail = useAppSelector(selectEmail);
  const experienceType = useAppSelector(selectExperienceType);
  const dispatch = useAppDispatch();
  const { navigateToCheckYourEmailPage, navigateToPage } = useCaseExternalNavigation();
  const [verifyUser, { isLoading, data, error }] = mboAPI.useLazyVerifyUserQuery();
  const [getUser, { isLoading: isUserDataLoading }] = mboAPI.useLazyGetUserDataQuery();
  const bookingFormData = useAppSelector(selectBookingForm);

  const [getBookingData] = mboAPI.useLazyRetrieveBookingDataQuery();
  const [locationId, setLocationId] = useState<string | undefined>(location);
  const accessToken = useAppSelector(selectAccessToken);
  const refreshToken = useAppSelector(selectRefreshToken);
  const isAlreadyLoggedIn = useMemo(
    () => !!(accessToken && refreshToken),
    [accessToken, refreshToken],
  );

  useEffect(() => {
    setLocationId(location);
  }, [location]);

  const logoutSignedInUser = () => {
    posthog?.reset();
    dispatch(clearAuthData());
    localStorage.removeItem(`persist:${getReduxStoreVersion()}`);
    localStorage.removeItem('new_email');
    localStorage.removeItem('email');
    posthog.reset();
  };

  const openMobileApp = (
    token: string | null,
    email: string | null,
    bookingUUID?: string | null,
  ) => {
    try {
      const appScheme = process.env.VITE_MOBILE_APP_SCHEME;
      if (appScheme) {
        if (window.confirm('Open the Five Iron app to finish verifying your account.')) {
          window.location.href = `${appScheme}://root/verify?token=${token}&email=${email}&uuid=${bookingUUID}`;
        }
      }
    } catch (_error) {
      console.error(_error);
      navigateToPage('/');
    }
  };

  const handleVerifyUser = (
    token: string | null,
    email: string | null,
    bookingUUID?: string | null,
    useMobile?: boolean,
  ) => {
    if (useMobile) {
      return openMobileApp(token, email, bookingUUID);
    }

    if (bookingUUID && bookingUUID !== 'undefined') {
      handleParseAndSaveBookingData(bookingUUID);
    }

    const userEmail = email ? email : storeEmail;
    if (token && userEmail) {
      dispatch(setToken(token));
      dispatch(setEmail({ email: decodeURIComponent(userEmail) }));
      verifyUser({ token, email: userEmail });

      // https://tagmanager.google.com/#/container/accounts/6112801006/containers/123456338/workspaces/10
      if (typeof window !== 'undefined' && typeof window.vgo !== 'undefined') {
        window.vgo('setEmail', userEmail);
        window.vgo('process');
      }
      localStorage.setItem('new_email', userEmail);
      Sentry.setUser({ email: userEmail });
    } else {
      navigateToCheckYourEmailPage(error);
    }
  };
  const handleParseAndSaveBookingData = async (bookingUUID: string) => {
    try {
      const retrievedBookingData = await getBookingData(bookingUUID).unwrap();
      setLocationId(retrievedBookingData.location);
      dispatch(setBookingWizardData(retrievedBookingData));
    } catch {
      dispatch(setShowError(true));
    }
  };
  const handleAccessToken = () => {
    if (data?.accessToken) {
      dispatch(setAccessToken(data?.accessToken));
    }
    if (data?.refreshToken) {
      dispatch(setRefreshToken(data?.refreshToken));
    }
  };

  const handleSaveUserDataToStore = (userSummary: UserTypes.UserSummary | undefined) => {
    dispatch(setUserSummary(userSummary));
    if (userSummary?.id) {
      posthog.identify(userSummary.id, {
        email: userSummary.email,
        firstName: userSummary.firstName,
        lastName: userSummary.lastName,
        clientId: userSummary.clientId,
      });
      if (userSummary.email) {
        posthog.capture('$merge_dangerously', {
          alias: userSummary.email,
          distinct_id: userSummary.id,
        });
      }
    }
  };

  const handleGetUser = () => {
    if (locationId) {
      return getUser(locationId).unwrap();
    }
  };

  const handleRedirectToCheckYourEmail = (error: UserVerificationError) => {
    navigateToCheckYourEmailPage(error);
  };

  const handleRedirectToConfirmReservation = () => {
    navigateToPage(`/${WizardStep.confirmReservation}`);
  };

  const handleNavigateToPaymentDetails = () => {
    navigateToPage(`/${WizardStep.paymentDetails}`);
  };

  const handleNavigateToSelectExperience = () => {
    navigateToPage(`/${WizardStep.selectExperience}`);
  };

  const handleNavigateToLocationSelect = () => {
    navigateToPage(`/${WizardStep.selectLocation}`);
  };

  const handleNavigateToChooseLessonTime = () => {
    navigateToPage(`/${WizardStep.chooseLessonTime}`);
  };

  const handleNavigateToSelectFittingStartTime = () => {
    navigateToPage(`/${WizardStep.selectFittingStartTime}`);
  };

  return {
    inProgress: isLoading,
    result: data,
    error: getError(error),
    isUserDataLoading,
    isAlreadyLoggedIn,
    handleSaveUserDataToStore,
    handleGetUser,
    handleAccessToken,
    redirectToConfirmReservation: handleRedirectToConfirmReservation,
    redirectToCheckYourEmail: handleRedirectToCheckYourEmail,
    redirectToPaymentDetails: handleNavigateToPaymentDetails,
    redirectToSelectExperience: handleNavigateToSelectExperience,
    redirectToLocationSelect: handleNavigateToLocationSelect,
    redirectToChooseLessonTime: handleNavigateToChooseLessonTime,
    redirectToSelectFittingStartTime: handleNavigateToSelectFittingStartTime,
    verifyUser: handleVerifyUser,
    logoutSignedInUser,
    bookingFormData,
    experienceType,
  };
};

export default useVerifyUser;
